import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseAsInteger, useQueryState } from "nuqs";
import AuthContext from "../../context/AuthProvider";
import AddCase from "../../components/AddCase";
import DashboardTop from "../../components/Updates/DashboardTop";
import useCases from "../../features/cases/useCases";
import useCaseFilters from "../../features/cases/useCaseFilters";
import {
  applyCaseFilters,
  matchesSearch,
  sortByColumn,
} from "../../features/cases/caseFilters";
import { ASTIKA_COLUMNS, ASTIKA_COLUMNS_BY_ID, FAV_COLUMN } from "../cases/caseColumns";
import CasesTable from "../cases/CasesTable";
import CasesToolbar from "../cases/CasesToolbar";
import TablePagination from "../cases/TablePagination";
import BulkActionBar from "../cases/BulkActionBar";
import useTablePrefs from "../cases/useTablePrefs";
import useRowSelection from "../cases/useRowSelection";
import useCaseMutations, { RECHECK_CAP } from "../cases/useCaseMutations";
import CaseDetailPanel from "../detail/CaseDetailPanel";
import useHotkeys from "../hooks/useHotkeys";
import ShortcutsHelp from "../ShortcutsHelp";

// The right pane shows either the case detail or the (narrower) overview;
// each keeps its own width. Bounds must match .ds-split / .ds-split--overview.
const PANE_SIZES = {
  detail: { key: "mysolon.ds.astika.panelWidth", min: 420, max: 900 },
  overview: { key: "mysolon.ds.astika.overviewWidth", min: 280, max: 640 },
};
const TABLE_MIN = 520;

const readPaneWidth = ({ key, min, max }) => {
  try {
    const value = Number(localStorage.getItem(key));
    return value >= min && value <= max ? value : null;
  } catch {
    return null;
  }
};

const readPaneWidths = () => ({
  detail: readPaneWidth(PANE_SIZES.detail),
  overview: readPaneWidth(PANE_SIZES.overview),
});

const isFav = (c) => c?.fav === 1 || c?.fav === true;

const uniqueSorted = (values) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, "el"));

const AstikaDesktop = () => {
  const navigate = useNavigate();
  const { credits, setCredits, checking } = useContext(AuthContext);
  const { allCases, loading, addCase, removeCase, updateCase } = useCases();
  const { filters, activeCount, search, page, setFilter, setSearch, clearFilters, setPage } =
    useCaseFilters();
  const [openId, setOpenId] = useQueryState("case", parseAsInteger);
  const { prefs, toggleColumn, resetColumns, cycleSort, setPageSize } = useTablePrefs(
    "astika",
    ASTIKA_COLUMNS
  );
  const [focusedId, setFocusedId] = useState(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [notice, setNotice] = useState(null);
  const [paneWidths, setPaneWidths] = useState(readPaneWidths);
  const searchRef = useRef(null);
  const splitRef = useRef(null);
  const noticeTimer = useRef(null);

  const notify = useCallback((text, tone = "ok") => {
    window.clearTimeout(noticeTimer.current);
    setNotice({ text, tone });
    noticeTimer.current = window.setTimeout(() => setNotice(null), 6000);
  }, []);
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const mutations = useCaseMutations({ allCases, updateCase, removeCase, notify });

  // ---- derived list -------------------------------------------------------
  const searched = useMemo(
    () => (search ? allCases.filter((c) => matchesSearch(c, search)) : allCases),
    [allCases, search]
  );
  const filtered = useMemo(() => applyCaseFilters(searched, filters), [searched, filters]);
  const sorted = useMemo(() => {
    if (!prefs.sort) return filtered; // allCases is already newest-first
    const column =
      prefs.sort.id === "fav" ? FAV_COLUMN : ASTIKA_COLUMNS_BY_ID[prefs.sort.id];
    return sortByColumn(filtered, column, prefs.sort.dir);
  }, [filtered, prefs.sort]);
  const orderedIds = useMemo(() => sorted.map((c) => c.id), [sorted]);

  const pageSize = prefs.pageSize;
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const rows = useMemo(
    () => sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sorted, currentPage, pageSize]
  );

  useEffect(() => {
    if (!loading && page > pageCount - 1) setPage(pageCount - 1);
  }, [loading, page, pageCount, setPage]);

  const visibleColumns = useMemo(
    () => prefs.columns.map((id) => ASTIKA_COLUMNS_BY_ID[id]).filter(Boolean),
    [prefs.columns]
  );

  const courts = useMemo(() => uniqueSorted(allCases.map((c) => c.court)), [allCases]);
  const diadikasies = useMemo(
    () => uniqueSorted(allCases.map((c) => c.diadikasia)),
    [allCases]
  );
  const hasEpan = useMemo(() => allCases.some((c) => c.epanaprosdiorismos), [allCases]);

  const pendingCheckIds = useMemo(
    () => new Set((Array.isArray(checking) ? checking : []).map((c) => String(c.case_id))),
    [checking]
  );

  const selection = useRowSelection(orderedIds);
  const selectedIds = useMemo(
    () => orderedIds.filter((id) => selection.selected.has(id)),
    [orderedIds, selection.selected]
  );

  const openCase = openId !== null ? allCases.find((c) => c.id === openId) : null;

  // ---- panel --------------------------------------------------------------
  const openInPanel = useCallback(
    (id) => {
      setFocusedId(id);
      // First open adds a history entry (Back closes the panel); moving
      // between cases while it is open replaces it.
      setOpenId(id, { history: openId === null ? "push" : "replace" });
    },
    [openId, setOpenId]
  );
  const closePanel = useCallback(() => setOpenId(null, { history: "replace" }), [setOpenId]);

  // Restore the list title when the panel closes.
  useEffect(() => {
    if (openId === null) document.title = "Αστικά | MySolon.gr";
  }, [openId]);

  // Deep links (?case=…) focus the opened case.
  useEffect(() => {
    if (openId !== null) setFocusedId(openId);
  }, [openId]);

  const handleRowClick = useCallback(
    (id, e) => {
      if (e?.ctrlKey || e?.metaKey) {
        window.open(`/cases/${id}`, "_blank", "noopener");
        return;
      }
      openInPanel(id);
    },
    [openInPanel]
  );

  const handleDelete = useCallback(
    async (id) => {
      const deleted = await mutations.deleteOne(id);
      if (deleted && openId === id) closePanel();
    },
    [mutations, openId, closePanel]
  );

  // ---- selection / bulk ---------------------------------------------------
  const handleSelectPage = useCallback(
    (checked) => selection.setMany(rows.map((r) => r.id), checked),
    [selection, rows]
  );

  const handleBulkRecheck = useCallback(async () => {
    if (selectedIds.length > RECHECK_CAP && selectedIds.length === allCases.length) {
      if (
        window.confirm(
          "Επιλέξατε όλες τις υποθέσεις. Να ξεκινήσει ο μαζικός έλεγχος όλων (επιτρέπεται μία φορά την ημέρα);"
        )
      ) {
        await mutations.checkAll();
      }
      return;
    }
    await mutations.bulkRecheck(selectedIds);
  }, [selectedIds, allCases.length, mutations]);

  const handleBulkDelete = useCallback(async () => {
    const deleted = await mutations.bulkDelete(selectedIds);
    if (deleted.length) {
      selection.clear();
      if (openId !== null && deleted.includes(openId)) closePanel();
    }
  }, [mutations, selectedIds, selection, openId, closePanel]);

  // ---- keyboard -----------------------------------------------------------
  const moveFocus = useCallback(
    (delta) => {
      if (!orderedIds.length) return;
      const current = focusedId !== null ? orderedIds.indexOf(focusedId) : -1;
      let nextIndex;
      if (current === -1) {
        nextIndex = delta > 0 ? currentPage * pageSize : Math.min(orderedIds.length, (currentPage + 1) * pageSize) - 1;
      } else {
        nextIndex = Math.max(0, Math.min(orderedIds.length - 1, current + delta));
      }
      const nextId = orderedIds[nextIndex];
      const nextPage = Math.floor(nextIndex / pageSize);
      if (nextPage !== currentPage) setPage(nextPage);
      setFocusedId(nextId);
      if (openId !== null) setOpenId(nextId, { history: "replace" });
    },
    [orderedIds, focusedId, currentPage, pageSize, setPage, openId, setOpenId]
  );

  const targetId = focusedId ?? openId;

  useHotkeys(
    {
      j: () => moveFocus(1),
      ArrowDown: () => moveFocus(1),
      k: () => moveFocus(-1),
      ArrowUp: () => moveFocus(-1),
      Enter: () => (focusedId !== null ? openInPanel(focusedId) : false),
      o: () => (targetId !== null ? navigate(`/cases/${targetId}`) : false),
      "Shift+Enter": () => (targetId !== null ? navigate(`/cases/${targetId}`) : false),
      "/": () => searchRef.current?.focus(),
      Escape: (e) => {
        if (e.target === searchRef.current) {
          searchRef.current.blur();
          return true;
        }
        if (openId !== null) return closePanel();
        if (selection.selected.size) return selection.clear();
        return false;
      },
      x: () => (focusedId !== null ? selection.toggle(focusedId) : false),
      s: () => (targetId !== null ? mutations.toggleFav(targetId) : false),
      r: () => (targetId !== null ? mutations.recheckOne(targetId) : false),
      "[": () => setPage(Math.max(0, currentPage - 1)),
      "]": () => setPage(Math.min(pageCount - 1, currentPage + 1)),
      "?": () => setShowHelp(true),
    },
    { enabled: !showAddCase && !showHelp }
  );

  // ---- resizable split ----------------------------------------------------
  const paneMode = openId === null ? "overview" : "detail";

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const container = splitRef.current;
      if (!container) return;
      const { key, min, max: paneMax } = PANE_SIZES[paneMode];
      let width = null;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        const max = Math.min(paneMax, rect.width - TABLE_MIN);
        width = Math.max(min, Math.min(max, rect.right - ev.clientX));
        setPaneWidths((prev) => ({ ...prev, [paneMode]: width }));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.classList.remove("ds-resizing");
        try {
          if (width) localStorage.setItem(key, String(Math.round(width)));
        } catch {}
      };
      document.body.classList.add("ds-resizing");
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [paneMode]
  );

  const resetPaneWidth = useCallback(() => {
    setPaneWidths((prev) => ({ ...prev, [paneMode]: null }));
    try {
      localStorage.removeItem(PANE_SIZES[paneMode].key);
    } catch {}
  }, [paneMode]);

  // ---- add case modal -----------------------------------------------------
  const handleAddCaseModal = useCallback((e) => {
    // AddCase calls this with no args on success/close and with the keydown
    // event while typing; only Escape (or no event) closes it.
    if (!e?.key || e.key === "Escape") setShowAddCase(false);
  }, []);

  const handleAddCase = useCallback(
    (singleCase) => {
      addCase(singleCase);
      if (singleCase?.id) {
        setFocusedId(singleCase.id);
        notify("Η υπόθεση προστέθηκε.", "ok");
      }
    },
    [addCase, notify]
  );

  const paneWidth = paneWidths[paneMode];
  const splitStyle = paneWidth ? { "--ds-panel-w": `${paneWidth}px` } : undefined;

  return (
    <div className="ds-astika">
      <CasesToolbar
        ref={searchRef}
        search={search}
        onSearch={setSearch}
        filters={filters}
        onFilter={setFilter}
        activeFilterCount={activeCount}
        onClearFilters={clearFilters}
        courts={courts}
        diadikasies={diadikasies}
        hasEpan={hasEpan}
        total={allCases.length}
        filteredTotal={sorted.length}
        onAddCase={() => setShowAddCase(true)}
        onCheckAll={mutations.checkAll}
        bulkChecking={mutations.bulkChecking}
        columns={ASTIKA_COLUMNS}
        visibleColumnIds={prefs.columns}
        onToggleColumn={toggleColumn}
        onResetColumns={resetColumns}
        onShowHelp={() => setShowHelp(true)}
      />

      {selectedIds.length > 0 && (
        <BulkActionBar
          count={selectedIds.length}
          filteredTotal={sorted.length}
          allFav={selectedIds.every((id) => isFav(allCases.find((c) => c.id === id)))}
          progress={mutations.progress}
          onSelectAllFiltered={() => selection.setMany(orderedIds, true)}
          onFav={() => mutations.bulkFav(selectedIds)}
          onRecheck={handleBulkRecheck}
          onDelete={handleBulkDelete}
          onClear={selection.clear}
        />
      )}

      {notice && (
        <div className={`ds-notice ds-notice--${notice.tone}`} role="status">
          {notice.text}
          <button type="button" className="ds-link-btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}

      <div className={`ds-split ds-split--${paneMode}`} ref={splitRef} style={splitStyle}>
        <div className="ds-table-pane">
          <div className="ds-table-scroll">
            {loading ? (
              <div className="ds-empty">
                <span className="ds-spinner" aria-hidden="true" /> Φόρτωση υποθέσεων…
              </div>
            ) : allCases.length === 0 ? (
              <div className="ds-empty">
                Δεν έχετε καταχωρήσει υποθέσεις.
                <button
                  type="button"
                  className="ds-btn ds-btn--primary"
                  onClick={() => setShowAddCase(true)}
                >
                  Προσθήκη υπόθεσης
                </button>
              </div>
            ) : sorted.length === 0 ? (
              <div className="ds-empty">
                Δεν βρέθηκαν υποθέσεις με αυτά τα κριτήρια.
                {(activeCount > 0 || search) && (
                  <button
                    type="button"
                    className="ds-link-btn"
                    onClick={() => {
                      clearFilters();
                      setSearch("");
                    }}
                  >
                    Καθαρισμός αναζήτησης και φίλτρων
                  </button>
                )}
              </div>
            ) : (
              <CasesTable
                rows={rows}
                columns={visibleColumns}
                sort={prefs.sort}
                onSort={cycleSort}
                selected={selection.selected}
                onToggleSelect={selection.toggle}
                onSelectPage={handleSelectPage}
                focusedId={focusedId}
                openId={openId}
                onRowClick={handleRowClick}
                onToggleFav={mutations.toggleFav}
                pendingCheckIds={pendingCheckIds}
              />
            )}
          </div>
          {sorted.length > 0 && (
            <TablePagination
              page={currentPage}
              pageSize={pageSize}
              total={sorted.length}
              onPage={(p) => setPage(Math.max(0, Math.min(pageCount - 1, p)))}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          )}
        </div>

        <div
          className="ds-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Αλλαγή πλάτους πλαισίου"
          title="Σύρετε για αλλαγή πλάτους · διπλό κλικ για επαναφορά"
          onPointerDown={startResize}
          onDoubleClick={resetPaneWidth}
        />

        <div className="ds-detail-pane">
          {openId !== null ? (
            <CaseDetailPanel
              caseId={openId}
              rowCase={openCase}
              pendingCheck={pendingCheckIds.has(String(openId))}
              onClose={closePanel}
              onToggleFav={mutations.toggleFav}
              onRecheck={mutations.recheckOne}
              onDelete={handleDelete}
            />
          ) : (
            <div className="ds-overview">
              {filtered.length > 0 ? (
                <DashboardTop cases={filtered} columns={1} onOpenCase={openInPanel} />
              ) : (
                !loading && (
                  <div className="ds-empty">Επιλέξτε μια υπόθεση για να δείτε τα στοιχεία της.</div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {showAddCase && (
        <div
          className="ds-modal-backdrop"
          data-ds-modal-open="true"
          onMouseDown={() => setShowAddCase(false)}
        >
          <div
            className="ds-modal ds-modal--addcase"
            role="dialog"
            aria-modal="true"
            aria-label="Προσθήκη υπόθεσης"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <AddCase
              handleClickAddCaseModal={handleAddCaseModal}
              allCases={allCases}
              handleAddCase={handleAddCase}
              credits={credits}
              setCredits={setCredits}
            />
          </div>
        </div>
      )}

      {showHelp && <ShortcutsHelp onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default AstikaDesktop;
